import { ArtifactEditor } from "@/components/artifacts/ArtifactEditor.tsx";
import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { ProposedSlotSelector } from "@/components/scheduling/ProposedSlotSelector.tsx";
import { ProposedTimesCard } from "@/components/scheduling/ProposedTimesCard.tsx";
import { Button } from "@/components/ui/button.tsx";
import { parseISOToDate } from "@/lib/date-utils.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { type ReactNode, useState } from "react";

type KickoffBodyWithSlotsData = Extract<ArtifactComponent, { type: "kickoffBodyWithSlots" }>;

/**
 * Schema-driven block for the merged kickoff body, with the proposed-times
 * slot block visually embedded between the prose body and its closing line —
 * so the card preview matches the email layout the recipient sees.
 *
 * Preview rendering is split server-side via `findSlotInsertionPoint`:
 *   - `bodyBefore` = prose up to (but not including) the closing-line
 *     paragraph.
 *   - `bodyAfter` = closing-line paragraph onward (+ OKTHX promo footer).
 * The slot block renders between them. Editing toggles to one
 * `ArtifactEditor` for the full `messageBody` (prose only — no bullets,
 * no closing-paragraph split) — single editor, no split-body race.
 */
export const KickoffBodyWithSlotsBlock = ({
  component,
}: {
  component: KickoffBodyWithSlotsData;
}) => {
  const { isReadOnly, patch, patchOne } = useArtifactViewCtx();
  const editable = !isReadOnly && component.editable;
  const [isEditing, setIsEditing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Edit mode: one editor for the full prose body. The editor saves on blur
  // (patches `messageBody`), so the "Done" button only needs to exit edit mode —
  // the edit is already persisted by the time it's clicked.
  if (isEditing) {
    return (
      <div className="flex flex-col gap-2">
        <ArtifactEditor
          content={component.messageBody}
          readOnly={false}
          handleSave={async (markdown) => {
            await patchOne(component.messageBodyEditPath, markdown);
            setIsEditing(false);
          }}
          showDebug={false}
        />
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(false)}
            data-statsig-name="scheduling-kickoff-done-body-edit"
          >
            Done
          </Button>
        </div>
      </div>
    );
  }

  // Preview: read-only markdown halves with the slot block embedded.
  // The shared `ProposedTimesCard` renders as a button when `onEdit` is
  // supplied; otherwise as a plain div. Mount-conditional picker stays
  // local (`pickerOpen`) so FullCalendar + calendar queries don't load
  // until the user clicks the card.
  const slotBlock = (
    <ProposedTimesCard
      slots={component.slots}
      onEdit={editable ? () => setPickerOpen(true) : undefined}
      statsigName="scheduling-kickoff-edit-proposed-times"
    />
  );

  // Click anywhere in the prose to edit it (no separate "Edit body" button) —
  // the slot block keeps its own click (opens the time picker). Read-only
  // (sent / not editable) renders the prose plain, with no edit affordance.
  const clickToEdit = (children: ReactNode) =>
    editable ? (
      // biome-ignore lint/a11y/useSemanticElements: a <button> would nest the prose's <a> links (invalid interactive nesting); keyboard access is via tabIndex + onKeyDown.
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsEditing(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsEditing(true);
          }
        }}
        title="Click to edit the email body"
        data-statsig-name="scheduling-kickoff-edit-body"
        className="-mx-2 cursor-pointer rounded px-2 transition-colors hover:bg-muted/50"
      >
        {children}
      </div>
    ) : (
      children
    );

  return (
    <div className="flex flex-col">
      {/* bodyBefore: prose up to the closing-line paragraph. */}
      {component.bodyBefore.trim().length > 0 &&
        clickToEdit(
          <ArtifactEditor
            content={component.bodyBefore}
            readOnly={true}
            handleSave={async () => {}}
            showDebug={false}
          />,
        )}

      {/* Embedded slot block — opens the picker on click in edit-allowed
          state. Mount-conditional on `pickerOpen` to keep FullCalendar +
          calendar queries dormant until the user opens it. */}
      {slotBlock}

      {/* bodyAfter: closing line + signature + OKTHX promo footer. */}
      {component.bodyAfter.trim().length > 0 &&
        clickToEdit(
          <ArtifactEditor
            content={component.bodyAfter}
            readOnly={true}
            handleSave={async () => {}}
            showDebug={false}
          />,
        )}

      {pickerOpen && (
        <ProposedSlotSelector
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          slots={component.slots}
          duration={component.duration}
          initialDate={parseISOToDate(component.initialDateISO)}
          onSave={(slots, durationMinutes) => {
            // Slots + duration commit together: a duration change re-sized the
            // slots, so persisting one without the other would leave the artifact
            // internally inconsistent until the next edit.
            void patch([
              { path: component.slotsEditPath, value: slots },
              { path: component.durationEditPath, value: durationMinutes },
            ]);
          }}
        />
      )}
    </div>
  );
};
